import * as React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';

import { Autocomplete, Button, Chip, debounce, FormControl, Grid, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material';

import useAuth from 'hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import Halog from 'types/halog';
import HalogService from 'services/HalogService';
import { useCallback, useEffect, useState } from 'react';
import { dispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GenericSkeleton from 'ui-component/skeleton/GenericSkeleton';
import SubCard from 'ui-component/cards/SubCard';
import { gridSpacing } from 'store/constant';
import Tag from 'types/tag';
import TagService from 'services/TagService';
import ConfirmatioDialog from 'ui-component/ConfirmationDialog';
import useConfig from 'hooks/useConfig';
import Utilities from 'utils/utilities';

const HalogDetails = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [tags, setTags] = useState<Tag[]>([]);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
    const [tagToSearch, setTagToSearch] = useState<string>('');
    const [halog, setHalog] = useState<Halog | undefined>(undefined);
    const [halogCopy, setHalogCopy] = useState<Halog | undefined>(undefined);
    const theme = useTheme();
    const { locale } = useConfig();
    const utilities = new Utilities();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dialogDelete, setDialogDelete] = useState(false);
    const [dialogDeviceNotExist, setDialogDeviceNotExist] = useState(false);

    const halogService = new HalogService();
    const tagService = new TagService();

    useEffect(() => {
        (async () => {
            if (searchParams.get('id')!) {
                await fetchSingleElement();
            } else {
                setDialogDeviceNotExist(true);
            }
        })();
    }, []);

    useEffect(() => {
        const e = {
            ...halogCopy!,
            tags: selectedTags
        };
        setHalogCopy(e);
    }, [selectedTags]);

    useEffect(() => {
        (async () => {
            if (halogCopy !== undefined) {
                await fetchTags();
            }
        })();
    }, [halogCopy, tagToSearch, halog]);

    const fetchSingleElement = async () => {
        setIsLoading(true);
        const halogRetrieved = await halogService.getHalog(searchParams.get('id')!);
        if (halogRetrieved) {
            setHalog(halogRetrieved!);
            setHalogCopy(halogRetrieved!);
            setSelectedTags(halogRetrieved?.tags || []);
            await fetchTags();
        } else {
            setDialogDeviceNotExist(true);
        }
        setIsLoading(false);
    };

    const fetchTags = useCallback(
        debounce(async () => {
            if (halog && halog.id) {
                const tagResult = await tagService.getHalogTags(tagToSearch, halogCopy!.id, user?.tenantId!);
                setTags(tagResult);
            }
        }, 700),
        [tagToSearch, user?.tenantId, halog]
    );

    const handleTagChange = (_: React.SyntheticEvent, newValue: Tag[]) => {
        const uniqueTags = newValue
            .map((tag) => ({ ...tag, name: tag.name.trim() }))
            .filter((tag, index, self) => index === self.findIndex((t) => t.name.toLowerCase() === tag.name.toLowerCase()));
        setSelectedTags(uniqueTags);

        const removedTags = selectedTags.filter((tag) => !uniqueTags.some((selectedTag) => selectedTag.id === tag.id));

        setTags((prev) => {
            const uniqueTags = [...prev, ...removedTags].filter(
                (tag, index, self) => index === self.findIndex((t) => t.name.trim().toLowerCase() === tag.name.trim().toLowerCase())
            );
            return uniqueTags;
        });
    };

    const handleTagInputChange = (event: React.KeyboardEvent | React.FocusEvent, value: string) => {
        const trimmedValue = value.trim().toLowerCase();
        const containsComma = trimmedValue.includes(',');
        if (containsComma) {
            dispatch(startSnackbar(t('ERROR_ADDING_TAG_REMOVE_COMMA'), 'error'));
            return;
        }

        setTagToSearch(value);

        // Verifica se l'input è valido
        if (trimmedValue && !containsComma && (event.type === 'blur' || (event as React.KeyboardEvent).key === 'Enter')) {
            const existingTag = tags.find((tag) => tag.name.trim().toLowerCase() === trimmedValue);
            const alreadySelected = selectedTags.some((tag) => tag.name.trim().toLowerCase() === trimmedValue);

            if (!alreadySelected) {
                if (existingTag) {
                    setSelectedTags((prev) => [...prev, existingTag]);
                } else {
                    const newTag: Tag = { id: uuidv4(), name: trimmedValue };
                    setTags((prev) => [...prev, newTag]);
                    setSelectedTags((prev) => [...prev, newTag]);
                }
            }
        }
    };

    const onSave = async (halog: Halog) => {
        setIsLoading(true);
        const response = await halogService.editHalog(halog);
        if (response) {
            await fetchSingleElement();
            dispatch(startSnackbar(t('DEVICE_EDITED_SUCCESSFULLY'), 'success'));
        } else {
            setIsLoading(false);
        }
    };

    const onDelete = async (halog: Halog) => {
        setIsLoading(true);
        const result = await halogService.deleteHalog(halog.id);
        if (result) {
            dispatch(startSnackbar(t('DEVICE_DELETED_SUCCESSFULLY'), 'success'));
            navigate('/halogs');
        } else {
            setIsLoading(false);
            dispatch(startSnackbar(t('ERROR_DURING_DELETE_DEVICE'), 'error'));
        }
    };

    return !isLoading ? (
        <Grid container spacing={gridSpacing}>
            <Grid item xs={12}>
                <SubCard>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel> {t('NAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={halogCopy?.name}
                                    onChange={(d) => {
                                        const e = {
                                            ...halogCopy!,
                                            name: d.target.value
                                        };
                                        setHalogCopy(e);
                                    }}
                                    label={t('NAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel> {t('IDENTIFIER')}</InputLabel>
                                <OutlinedInput disabled defaultValue={halogCopy?.identifier} label={t('IDENTIFIER')} />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                id="size-small-filled-multi"
                                size="small"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '52px',
                                        padding: 0
                                    },
                                    '& .MuiFilledInput-root': { backgroundColor: '#fff' },
                                    '& .MuiFilledInput-root:hover': { backgroundColor: '#fff' },
                                    '& #size-small-filled-multi': { backgroundColor: 'secondary' }
                                }}
                                options={tags.filter(
                                    (tag) => !selectedTags.some((selected) => selected.name.toLowerCase() === tag.name.toLowerCase())
                                )}
                                getOptionLabel={(option) => option.name}
                                value={selectedTags}
                                onChange={handleTagChange}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return <Chip color="secondary" key={key} label={option.name} size="small" {...tagProps} />;
                                    })
                                }
                                onInputChange={(event: any, value, reason) => {
                                    if (reason === 'input') handleTagInputChange(event, value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Tags"
                                        onKeyDown={(event: any) => handleTagInputChange(event, event.target.value)}
                                        onBlur={(event) => handleTagInputChange(event, event.target.value)}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel> {t('OWNER_MANUFACTURER_NOTE')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={halogCopy?.ownerManufacturerNote}
                                    onChange={(d) => {
                                        const e = {
                                            ...halogCopy!,
                                            ownerManufacturerNote: d.target.value
                                        };
                                        setHalogCopy(e);
                                    }}
                                    label={t('OWNER_MANUFACTURER_NOTE')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </SubCard>
            </Grid>
            {halogCopy?.pairDate && (
                <Grid item xs={12}>
                    <SubCard title={t('PAIRED_INFO')}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel> {t('ASSET_NAME')}</InputLabel>
                                    <OutlinedInput disabled defaultValue={halogCopy?.assetName} label={t('ASSET_NAME')} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel> {t('PAIR_DATE')}</InputLabel>
                                    <OutlinedInput
                                        disabled
                                        defaultValue={utilities.formatDate(halogCopy?.pairDate, locale)}
                                        label={t('PAIR_DATE')}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
            )}
            <Grid
                container
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                    paddingLeft: '25px'
                }}
            >
                <Grid item>
                    <Button
                        style={{
                            backgroundColor: theme.palette.error.main,
                            borderRadius: 7,
                            border: '2px solid',
                            borderColor: theme.palette.error.main
                        }}
                        onClick={() => setDialogDelete(true)}
                    >
                        <Typography sx={{ color: 'white' }}> {t('DELETE')}</Typography>
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        onClick={() => navigate('/assets')}
                        autoFocus
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.secondary.main,
                            borderRadius: 7,
                            marginRight: 10
                        }}
                    >
                        <Typography sx={{ color: theme.palette.primary.main, textTransform: 'none' }}> {t('RETURN_TO_ASSETS')}</Typography>
                    </Button>
                    <Button
                        onClick={() => onSave(halogCopy!)}
                        variant="text"
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.primary.main,
                            borderRadius: 7,
                            background: theme.palette.primary.main
                        }}
                    >
                        <Typography sx={{ color: '#fff' }}> {t('SAVE')}</Typography>
                    </Button>
                </Grid>
            </Grid>
            <ConfirmatioDialog
                isVisible={dialogDelete}
                onClose={() => {
                    setDialogDelete(false);
                }}
                onConfirm={() => {
                    setDialogDelete(false);
                    onDelete(halog!);
                }}
            />
            <ConfirmatioDialog
                isVisible={dialogDeviceNotExist}
                titleText="DEVICE_NOT_EXIST"
                bodyText="DEVICE_NOT_EXIST_BODY"
                primaryButtonText="Ok"
                onConfirm={() => {
                    setDialogDeviceNotExist(false);
                    navigate('/halogs');
                }}
            />
        </Grid>
    ) : (
        <GenericSkeleton />
    );
};

export default HalogDetails;
